import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Header } from '@/components/layout/Header'
import { ScoreConfirmDialog } from '@/components/league'
import { usePageTitle } from '@/hooks/useDocumentHead'
import { 
  matchService, 
  formatScore, 
  type UserMatchSummary, 
  type MatchStatus,
  type ScoreSet,
} from '@/services/matchService'
import { 
  Loader2, 
  AlertCircle, 
  Trophy, 
  Clock, 
  AlertTriangle, 
  XCircle,
  Check,
  ChevronRight,
  Swords,
  History,
} from 'lucide-react'

type FilterTab = 'all' | 'pending' | 'completed'
type MatchFormat = 'bestOf1' | 'bestOf3' | 'bestOf5'

function getStatusBadge(status: MatchStatus, requiresAction: boolean) {
  if (requiresAction) {
    return (
      <Badge variant="default" className="bg-orange-500 hover:bg-orange-500">
        <AlertTriangle className="h-3 w-3 mr-1" />
        Action Required
      </Badge>
    )
  }

  switch (status) {
    case 'pending':
      return (
        <Badge variant="secondary">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </Badge>
      )
    case 'approved':
      return (
        <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
          <Check className="h-3 w-3 mr-1" />
          Confirmed
        </Badge>
      )
    case 'disputed':
      return (
        <Badge variant="destructive">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Disputed
        </Badge>
      )
    case 'cancelled':
      return (
        <Badge variant="outline" className="text-muted-foreground">
          <XCircle className="h-3 w-3 mr-1" />
          Cancelled
        </Badge>
      )
    default:
      return null
  }
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    return 'Today'
  } else if (diffDays === 1) {
    return 'Yesterday'
  } else if (diffDays < 7) {
    return `${diffDays} days ago`
  } else {
    return date.toLocaleDateString(undefined, { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
    })
  }
}

export function MyMatches() {
  usePageTitle('My Matches')
  const navigate = useNavigate()
  const [matches, setMatches] = useState<UserMatchSummary[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<FilterTab>('all')
  const [scoreDialog, setScoreDialog] = useState<{
    open: boolean
    mode: 'accept' | 'dispute'
    matchId: string
    bracketId: string
    submitterName?: string
    score?: string
    sets?: ScoreSet[]
    matchFormat?: MatchFormat
  }>({
    open: false,
    mode: 'accept',
    matchId: '',
    bracketId: '',
  })

  const hasFetched = useRef(false)

  const fetchMatches = useCallback(async (force = false) => {
    if (hasFetched.current && !force) return
    hasFetched.current = true
    
    setIsLoading(true)
    setError(null)
    try {
      const response = await matchService.listMyMatches({ limit: 100 })
      setMatches(response.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load matches')
      hasFetched.current = false // Allow retry on error
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchMatches()
  }, [fetchMatches])

  const handleConfirmScore = (match: UserMatchSummary) => {
    setScoreDialog({
      open: true,
      mode: 'accept',
      matchId: match.matchId,
      bracketId: match.bracketId,
      submitterName: match.opponent.displayName,
      score: formatScore(match.sets),
      sets: match.sets,
    })
  }

  const handleDisputeScore = (match: UserMatchSummary) => {
    setScoreDialog({
      open: true,
      mode: 'dispute',
      matchId: match.matchId,
      bracketId: match.bracketId,
      submitterName: match.opponent.displayName,
      score: formatScore(match.sets),
      sets: match.sets,
    })
  }

  const handleDialogSuccess = () => {
    fetchMatches(true)
  }

  // Filter matches
  const filteredMatches = matches.filter(match => {
    if (filter === 'pending') {
      return match.status === 'pending'
    } else if (filter === 'completed') {
      return match.status === 'approved' || match.status === 'disputed' || match.status === 'cancelled'
    }
    return true
  })

  // Count pending actions
  const pendingCount = matches.filter(m => m.requiresAction).length

  const renderMatchCard = (match: UserMatchSummary) => {
    const scoreDisplay = formatScore(match.sets)

    return (
      <Card 
        key={match.matchId}
        className={`transition-colors cursor-pointer hover:bg-muted/50 ${match.requiresAction ? 'border-orange-200 bg-orange-50/30 hover:bg-orange-50/50' : ''}`}
        onClick={() => navigate(`/group/${match.groupId}/league/${match.leagueId}`)}
      >
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            {/* Opponent avatar */}
            <Avatar className="h-12 w-12">
              <AvatarImage src={match.opponent.avatar} alt={match.opponent.displayName} />
              <AvatarFallback>{getInitials(match.opponent.displayName)}</AvatarFallback>
            </Avatar>

            {/* Match details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="font-medium truncate">{match.opponent.displayName}</span>
                {getStatusBadge(match.status, match.requiresAction)}
              </div>
              
              <div className="flex items-center gap-3 text-sm flex-wrap">
                {/* Win/Loss indicator */}
                {match.status === 'approved' && (
                  <span className={`font-semibold ${match.won ? 'text-green-600' : 'text-red-500'}`}>
                    {match.won ? (
                      <span className="flex items-center gap-1">
                        <Trophy className="h-3.5 w-3.5" />
                        Won
                      </span>
                    ) : (
                      'Lost'
                    )}
                  </span>
                )}
                
                {/* Score */}
                <span className="text-muted-foreground font-mono">{scoreDisplay}</span>
                
                {/* Date */}
                <span className="text-muted-foreground">
                  {formatDate(match.playedAt || match.submittedAt)}
                </span>
              </div>

              {/* Group / League / Season context */}
              <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1 flex-wrap">
                {match.groupName && (
                  <span className="inline-flex items-center">
                    {match.groupName}
                  </span>
                )}
                {match.groupName && match.leagueName && <span>•</span>}
                {match.leagueName && (
                  <span>{match.leagueName}</span>
                )}
                {match.leagueName && match.seasonName && <span>•</span>}
                {match.seasonName && (
                  <span>{match.seasonName}</span>
                )}
              </p>
            </div>

            {/* Action buttons or chevron */}
            <div className="flex items-center gap-2 shrink-0">
              {match.requiresAction ? (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleConfirmScore(match)
                    }}
                  >
                    Confirm
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDisputeScore(match)
                    }}
                  >
                    Dispute
                  </Button>
                </div>
              ) : (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  const renderEmptyState = () => (
    <Card className="p-8">
      <div className="text-center text-muted-foreground">
        <Swords className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p className="font-medium">No matches yet</p>
        <p className="text-sm mt-1">
          {filter === 'pending' 
            ? 'No matches waiting for your action'
            : filter === 'completed'
            ? 'No completed matches found'
            : 'Your match history will appear here'}
        </p>
      </div>
    </Card>
  )

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <History className="h-6 w-6" />
              My Matches
            </h1>
            <p className="text-muted-foreground">
              {pendingCount > 0 
                ? `${pendingCount} match${pendingCount !== 1 ? 'es' : ''} need${pendingCount === 1 ? 's' : ''} your action`
                : `${matches.length} match${matches.length !== 1 ? 'es' : ''} played`
              }
            </p>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={filter === 'all' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setFilter('all')}
          >
            All
            <Badge variant="outline" className="ml-1.5 h-5 px-1.5">
              {matches.length}
            </Badge>
          </Button>
          <Button
            variant={filter === 'pending' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setFilter('pending')}
            className={pendingCount > 0 ? 'relative' : ''}
          >
            Pending
            {pendingCount > 0 && (
              <Badge className="ml-1.5 h-5 px-1.5 bg-orange-500 hover:bg-orange-500">
                {pendingCount}
              </Badge>
            )}
          </Button>
          <Button
            variant={filter === 'completed' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setFilter('completed')}
          >
            Completed
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <Card className="p-8">
            <div className="text-center text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Failed to load matches</p>
              <p className="text-sm mt-1">{error}</p>
              <Button variant="outline" className="mt-4" onClick={() => fetchMatches(true)}>
                Try again
              </Button>
            </div>
          </Card>
        ) : filteredMatches.length === 0 ? (
          renderEmptyState()
        ) : (
          <div className="space-y-2">
            {filteredMatches.map(renderMatchCard)}
          </div>
        )}
      </main>

      {/* Score Confirmation Dialog */}
      <ScoreConfirmDialog
        open={scoreDialog.open}
        onOpenChange={(open) => setScoreDialog(prev => ({ ...prev, open }))}
        mode={scoreDialog.mode}
        matchId={scoreDialog.matchId}
        bracketId={scoreDialog.bracketId}
        submitterName={scoreDialog.submitterName}
        score={scoreDialog.score}
        sets={scoreDialog.sets}
        matchFormat={scoreDialog.matchFormat}
        onSuccess={handleDialogSuccess}
      />
    </div>
  )
}
